// Shared helpers used by the Phase 2 extracted stateful intelligence
// components. Moved verbatim from DecisionEnginePage.js during the System
// Consolidation — no logic changes.

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local digest-history cache — last 8 previewed weekly digests per browser.
export const DIGEST_HISTORY_KEY = 'pfap_weekly_digest_history';
export const DIGEST_HISTORY_MAX = 8;

export const readDigestHistory = () => {
  try {
    const raw = localStorage.getItem(DIGEST_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const writeDigestHistory = (digest) => {
  if (!digest) return readDigestHistory();
  const shorten = (s, n = 90) =>
    !s ? '' : (s.length > n ? s.slice(0, n - 1) + '…' : s);
  const entry = {
    id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    total_decisions: digest.total_decisions,
    followed_rate: digest.followed_rate,
    positive_outcome_rate: digest.positive_outcome_rate,
    insight: shorten(digest.top_insight, 80),
    highlight: shorten(digest.key_highlight, 120),
    // Full snapshot for read-only reopen — reused by the modal.
    snapshot: {
      ...digest,
      _historical: true,
      _captured_at: new Date().toISOString(),
    },
  };
  const existing = readDigestHistory();
  // De-dup by minute-level timestamp (prevents double-entry if user opens the
  // modal twice in quick succession).
  const bucket = entry.timestamp.slice(0, 16); // YYYY-MM-DDTHH:MM
  const deduped = existing.filter((e) => (e.timestamp || '').slice(0, 16) !== bucket);
  const next = [entry, ...deduped].slice(0, DIGEST_HISTORY_MAX);
  try {
    localStorage.setItem(DIGEST_HISTORY_KEY, JSON.stringify(next));
  } catch (e) {
    // Quota / serialisation failure → silently noop, cache is non-critical.
  }
  return next;
};

export const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  return `${wk}w ago`;
};

// Trend-direction helper used by the local digest-history pill. Walks the
// history newest-first; first non-flat step sets direction, flats pass
// through, opposing step breaks the streak.
export const computeFollowedStreak = (hist) => {
  if (!hist || hist.length < 2) return { kind: 'none', weeks: 0 };
  let direction = null;
  let weeks = 0;
  for (let i = 0; i < hist.length - 1; i++) {
    const curr = hist[i]?.followed_rate ?? 0;
    const prev = hist[i + 1]?.followed_rate ?? 0;
    if (curr > prev) {
      if (direction === 'down') break;
      direction = 'up';
      weeks += 1;
    } else if (curr < prev) {
      if (direction === 'up') break;
      direction = 'down';
      weeks += 1;
    }
    // equal → continue without changing direction or breaking
  }
  if (!direction || weeks === 0) return { kind: 'none', weeks: 0 };
  return { kind: direction, weeks };
};
